/**
 * useImport Hook
 * Upravljanje import procesom
 */

import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FieldTransformOptions } from '../../../lib/xmlParser';
import { CustomField, ImportProgress, ImportResults, ImportMode, RowFilter } from '../types';
import { runImport as executeImport, validateImportConfig } from '../utils/importUtils';

export interface UseImportReturn {
  // State
  importing: boolean;
  progress: ImportProgress;
  results: ImportResults | null;

  // Actions
  runImport: (params: {
    xmlContent: string;
    mappings: Record<string, string>;
    fieldOptions: Record<string, FieldTransformOptions>;
    customFields: CustomField[];
    feedName: string;
    updateFields: Record<string, boolean>;
    profileId?: number;
    importMode?: ImportMode;
    sampleRecords?: any[];
    rowFilters?: RowFilter[];
  }) => Promise<void>;
  clearResults: () => void;
}

export function useImport(): UseImportReturn {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress>({ current: 0, total: 0 });
  const [results, setResults] = useState<ImportResults | null>(null);

  /**
   * Pokreni import
   */
  const runImport = useCallback(async (params: {
    xmlContent: string;
    mappings: Record<string, string>;
    fieldOptions: Record<string, FieldTransformOptions>;
    customFields: CustomField[];
    feedName: string;
    updateFields: Record<string, boolean>;
    profileId?: number;
    importMode?: ImportMode;
    sampleRecords?: any[];
    rowFilters?: RowFilter[];
  }) => {
    // Validate
    const errors = validateImportConfig(params);
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    setImporting(true);
    setResults(null);
    setProgress({ current: 0, total: 0 });

    try {
      const importResults = await executeImport(params, {
        onProgress: setProgress,
      });

      setResults(importResults);

      if (importResults.errors > 0) {
        toast.warning(
          `Import završen s greškama: ${importResults.created} kreirano, ` +
          `${importResults.updated} ažurirano, ${importResults.errors} grešaka`
        );
      } else {
        toast.success(
          `Import završen: ${importResults.created} kreirano, ${importResults.updated} ažurirano`
        );
      }
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error('Greška pri importu: ' + error.message);
      setResults({
        created: 0,
        updated: 0,
        errors: 1,
        errorDetails: [error.message],
      });
    } finally {
      setImporting(false);
    }
  }, []);

  /**
   * Očisti rezultate
   */
  const clearResults = useCallback(() => {
    setResults(null);
    setProgress({ current: 0, total: 0 });
  }, []);

  return {
    importing,
    progress,
    results,
    runImport,
    clearResults,
  };
}
